import React from "react";
import AwesomeSlider from "react-awesome-slider";
import AwsSliderStyles from "react-awesome-slider/src/core/styles.scss";
import { Footer, PageLayout } from "src/components/PageLayout";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { isMobile } from "react-device-detect";
import {
  AmplitudeEvent,
  logAmplitudeEventWithData,
} from "src/components/Amplitude";

type Author = {
  name: string;
  url: string;
};

const byline = JSON.parse(process.env.REACT_APP_AUTHOR as string) as Author[];

const OutboundLink: React.FC<{ to: string; children: React.ReactNode }> = ({
  to,
  children,
}) => (
  <a href={to} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

export const Homepage = () => (
  <PageLayout>
    <AwesomeSlider
      style={AwsSliderStyles}
      fillParent
      bullets={isMobile}
      infinite={false}
      onTransitionEnd={(e) => {
        logAmplitudeEventWithData(
          e.currentIndex === 0
            ? "viewLandingPage"
            : (`viewSlide${e.currentIndex}` as AmplitudeEvent),
          { mobile: isMobile }
        );
      }}
    >
      {/* LANDING PAGE */}
      <div className="scroll-container">
        <div className="container">
          <div className="copy-container">
            <div className="photo cover-photo">
              <LazyLoadImage
                src={require("../assets/images/missing_them_grid.jpg")}
                alt="Various images of children featured in this story"
              />
            </div>{" "}
          </div>
          <h1 className="headline">
            Kids Who Lost Parents to COVID, In Their Own Words
          </h1>
          <h2 className="deck">
            More than 8,700 children in New York City are grieving a parent or
            caregiver who died of COVID. Here are some of their favorite
            memories, and what they wish others knew.
          </h2>
          <div className="attribution">
            <p className="byline">
              By{" "}
              {byline.map((author, i) => (
                <span key={i} className="author">
                  <OutboundLink to={author.url}>{author.name}</OutboundLink>
                  {i < byline.length - 2
                    ? ", "
                    : i < byline.length - 1
                    ? " and "
                    : ""}
                </span>
              ))}
            </p>
          </div>{" "}
          <p className="article-date">
            Published Jan 26, 2023
          </p>
        </div>{" "}
      </div>

      {/* SLIDE 1 */}
      <div className="scroll-container">
        <div className="copy-container">
          <p className="copy">
            Across New York City, more than 8,700 children have{" "}
            <OutboundLink to="https://www.thecity.nyc/2023/1/26/23571588/thousands-nyc-children-whose-parent-died-from-covid-need-help">
              lost a parent or caregiver to COVID
            </OutboundLink>
            , according to researchers at the COVID Collaborative. Black,
            Hispanic and Asian children across the city were roughly three times
            more likely to have a caregiver die than their white peers.
            Immigrant communities across the city were also hit
            disproportionately by COVID.
          </p>
          <div className="photo">
            <LazyLoadImage
              src={require("../assets/images/maddie_fletcher_drawing.jpeg")}
              alt="A postcard that reads: Dear Nurse, Thank you for helping my papa. I wish I can visit but I can't. I'm praying for you to help him everyday. I love you papa. Love Madison."
            />
            <figcaption>Madison Fletcher</figcaption>
          </div>
          <p className="copy">
            THE CITY’s Missing Them project and its partners spoke to six
            children from immigrant families who lost a parent or caregiver to
            COVID. All of them lost a parent or caregiver within an 11-day span
            in April 2020, as COVID deaths peaked in New York City. We asked
            them to share their favorite memories about their loved ones, and
            what they wish their friends and teachers knew about how they are
            coping with loss. Here’s what they said.
          </p>
          <p className="copy">
            <em>The conversations have been edited for clarity.</em>
          </p>
        </div>
      </div>

      {/* SLIDE 2 */}
      <div className="scroll-container">
        <div className="copy-container">
          <h2>Madison “Maddie” Fletcher, 10</h2>
          <p className="copy">
            Her father,{" "}
            <OutboundLink to="https://projects.thecity.nyc/covid-19-deaths/#joseph-trevor-fletcher">
              Joseph Trevor Fletcher
            </OutboundLink>
            , died on April 11, 2020
          </p>
          <div className="photo">
            <LazyLoadImage
              src={require("../assets/images/maddie_fletcher_1.jpg")}
              alt="A postcard that reads: Dear Nurse, Thank you for helping my papa. I wish I can visit but I can't. I'm praying for you to help him everyday. I love you papa. Love Madison."
            />
            <figcaption>Courtesy of Veronica Fletcher</figcaption>
          </div>
          <p className="copy">
            <b>What’s your favorite memory with your dad?</b>
          </p>
          <p className="copy">
            We had this Girl Scouts daddy-daughter paint night. My mom got black
            aprons and put puffy paint and glitter and stickers and stuff on
            them. My dad and I made a sunset out of pink and red. We painted a
            shadow figure and we got to paint little tiaras on top and we got to
            use stickers. At the end, we swapped pictures. The picture that I
            made, I gave him, and the picture that he made, he gave to me.
          </p>
          <div className="photo is-small">
            <LazyLoadImage
              src={require("../assets/images/maddie_fletcher_girl_scouts.jpeg")}
              alt="."
            />
            <figcaption>Courtesy of Veronica Fletcher</figcaption>
          </div>
          <p className="copy">
            <b>
              If you could tell other kids what it’s been like to lose your
              parent to COVID, what would you say?
            </b>
          </p>
          <p className="copy">
            At first it's hard, but then it gets easier and easier. I guess it
            gets kind of easier but the damage is still there.
          </p>
          <p className="copy">
            <b>
              What advice would you have for other kids who are going through
              the same thing?
            </b>
          </p>
          <p className="copy">
            I can tell them they can talk with me or that we can draw pictures
            together. If you draw a picture, it's like letting your emotions out
            onto a piece of paper and it just stays there.
          </p>
        </div>
      </div>

      {/* SLIDE 3 */}
      <div className="scroll-container">
        <div className="copy-container">
          <h2>Zachary “Ziggy” Fletcher, 14</h2>
          <p className="copy">
            His father,{" "}
            <OutboundLink to="https://projects.thecity.nyc/covid-19-deaths/#joseph-trevor-fletcher">
              Joseph Trevor Fletcher
            </OutboundLink>
            , died on April 11, 2020
          </p>
          <div className="photo">
            <LazyLoadImage
              src={require("../assets/images/ziggy_fletcher_crop.jpg")}
              alt="Ziggy stands outside wearing a jacket"
            />
            <figcaption>Courtesy of Veronica Fletcher</figcaption>
          </div>
          <p className="copy">
            <b>What’s your favorite memory with your dad?</b>
          </p>
          <p className="copy">
            We went on a cruise — it was called the Breakaway — for someone's
            birthday. That was the best cruise. Papa taught me how to float on
            my back. Another one of my favorites was when we all went snorkeling
            together and got photos with the fishes.
          </p>
          <p className="copy">
            <b>Is there any object that reminds you of him?</b>
          </p>
          <p className="copy">
            Sometimes when I'm in my room playing with my Legos, I'll see the
            one Lego set and it reminds me of when I would build with my papa.
            Sometimes, after a while, I break it apart and build something out
            of it, something else.
          </p>
          <p className="copy">
            <b>
              If you could tell your classmates one thing about what it’s been
              like to lose a parent to COVID, what would you say?
            </b>
          </p>
          <p className="copy">It isn’t easy.</p>
          <p className="copy">
            <b>What about your teachers?</b>
          </p>
          <p className="copy">
            It's gonna take a while for you to adjust to losing them.
          </p>
          <p className="copy">
            <b>
              What advice do you have for other kids who are going through the
              same thing?
            </b>
          </p>
          <p className="copy">
            It's gonna get easier for you. Even if you think that you're at your
            worst, just keep going and it will get better.
          </p>
        </div>
      </div>

      {/* SLIDE 4 */}
      <div className="scroll-container">
        <div className="copy-container">
          <h2>Joshua Fletcher, 17</h2>
          <p className="copy">
            His father,{" "}
            <OutboundLink to="https://projects.thecity.nyc/covid-19-deaths/#joseph-trevor-fletcher">
              Joseph Trevor Fletcher
            </OutboundLink>
            , died on April 11, 2020
          </p>
          <div className="photo">
            <LazyLoadImage
              src={require("../assets/images/joshua_fletcher_crop.jpeg")}
              alt="Joshua looks at the camera wearing a blue hoodie and jacket"
            />
            <figcaption>Courtesy of Veronica Fletcher</figcaption>
          </div>
          <p className="copy">
            <b>What’s your favorite memory with your dad?</b>
          </p>
          <p className="copy">
            When he showed me what bus I needed to take to go to school — it was
            like he gave me what I needed for when he wouldn't be here.
          </p>
          <p className="copy">
            <b>
              If you could tell your classmates one thing about what it’s been
              like to lose a parent to COVID, what would you say?
            </b>
          </p>
          <p className="copy">
            It can be rough and challenging to go through, and you don't have to
            do it alone.
          </p>
          <div className="photo">
            <LazyLoadImage
              src={require("../assets/images/112122_missing_them_fletcher_family_2.jpg")}
              alt="A tablet showing a photo of Joseph Fletcher"
            />
            <figcaption>Ben Fractenberg/THE CITY</figcaption>
          </div>
          <p className="copy">
            <b>And what about teachers? What would you tell them?</b>
          </p>
          <p className="copy">
            I guess check in on them and see how they're feeling.
          </p>
          <p className="copy">
            <b>What advice would you give to other kids who lost a parent?</b>
          </p>
          <p className="copy">
            Don't lose sight of the things that you want to do. It's like any
            other obstacle or something that makes you stronger.
          </p>
        </div>
      </div>

      {/* SLIDE 5 */}
      <div className="scroll-container">
        <div className="copy-container">
          <h2>Alejandro Amaya, 10</h2>
          <p className="copy">
            His uncle,{" "}
            <OutboundLink to="https://projects.thecity.nyc/covid-19-deaths/#victor-humberto-heras">
              Victor Humberto Heras
            </OutboundLink>
            , died on April 19, 2020
          </p>
          <div className="photo">
            <LazyLoadImage
              src={require("../assets/images/120622_alejandro_amaya_4.jpeg")}
              alt="Alejandro holds up a photo of his uncle kissing his forehead"
            />
            <figcaption>Ben Fractenberg/THE CITY</figcaption>
          </div>
          <p className="copy">
            <b>What’s your favorite memory with your uncle?</b>
          </p>
          <p className="copy">
            He would pick me and my sister up from school. Sometimes, he took us
            to the park. We always went to my uncle’s apartment. Every time we
            were there, he’d ask us: “What do you want to eat?” He would cook us
            rice and beans. We’d spend time with him, playing games like cards,
            Uno or Monopoly.
          </p>
          <p className="copy">
            <b>
              If you could tell your friends one thing about what it’s been like
              to lose your uncle to COVID, what would you say?
            </b>
          </p>
          <p className="copy">
            When I found out that my uncle died, it was a sad day and I will
            never forget. It was the saddest day of my life.
          </p>
          <div className="photo">
            <LazyLoadImage
              src={require("../assets/images/120622_humberto_memorial_1.jpg")}
              alt="A framed photo of Victor sits on an altar"
            />
            <figcaption>Ben Fractenberg/THE CITY</figcaption>
          </div>
          <p className="copy">
            <b>
              What do you do to feel better when you’re sad and missing him?
            </b>
          </p>
          <p className="copy">
            I think about him. I sit and think about the good memories I have
            about him. At school, Miss Kassandra [my counselor] helps me when I
            get mad or sad. She helps me to breathe in and out. Miss Kassandra
            always says: Put your left fist like this and then put your right
            one like this. Your left is a flower, your right is a candle. You
            smell the flower, you blow on the candle. And you keep repeating.
            When I do that, I feel better. It makes me calm down and
            concentrate.
          </p>
          <p className="copy">
            <b>
              What advice do you have for other kids who are going through the
              same thing?
            </b>
          </p>
          <p className="copy">
            My advice to other kids that are sad would be to cherish the moments
            with our loved ones and family because you don't know if that can be
            the last time you got with them.
          </p>
        </div>
      </div>

      {/* SLIDE 6 */}
      <div className="scroll-container">
        <div className="copy-container">
          <h2>Rana Ibrahim, 17</h2>
          <p className="copy">
            Her mother,{" "}
            <OutboundLink to="https://projects.thecity.nyc/covid-19-deaths/#fatma-atia">
              Fatma Atia
            </OutboundLink>
            , died on April 9, 2020
          </p>
          <div className="photo">
            <LazyLoadImage
              src={require("../assets/images/012023_06_ibrahim.jpeg")}
              alt="Rana sits outside in a red sweater and looks at the camera"
            />
            <figcaption>Hiram Alejandro Durán/THE CITY</figcaption>
          </div>
          <p className="copy">
            <b>
              If you could tell your friends or classmates what it’s been like
              to lose a parent to COVID, what would you say?
            </b>
          </p>
          <p className="copy">
            I’d tell them to not take their parents for granted. Because a lot
            of times, people will. Then they lose them and they realize that
            they needed them.
          </p>
          <p className="copy">
            <b>And what would you tell your teachers?</b>
          </p>
          <p className="copy">
            I would tell them that it's hard and sometimes I need a break from
            school and stuff. I feel like sometimes the teachers don't really
            understand. They think that just because it happened, like, in 2020
            that it is not relevant anymore.
          </p>
          <p className="copy">
            <b>What’s one of your favorite memories with your mom?</b>
          </p>
          <p className="copy">
            Talking with her about mother-daughter stuff, like feminine stuff,
            in her room.
          </p>
          <p className="copy">
            <b>What advice do you have for other kids who lost parents?</b>
          </p>
          <p className="copy">
            I'd tell them even though it's difficult now, they should still have
            hope. Because there’s still reason to keep going and just to make
            them proud even though they're not there with you.
          </p>
        </div>
      </div>

      {/* SLIDE 7 */}
      <div className="scroll-container">
        <div className="copy-container">
          <h2>Ibrahim Ibrahim, 13</h2>
          <p className="copy">
            His mother,{" "}
            <OutboundLink to="https://projects.thecity.nyc/covid-19-deaths/#fatma-atia">
              Fatma Atia
            </OutboundLink>
            , died on April 9, 2020
          </p>
          <div className="photo">
            <LazyLoadImage
              src={require("../assets/images/012023_02_ibrahim.jpeg")}
              alt="Ibrahim stands outside in a red jacket and looks at the camera"
            />
            <figcaption>Hiram Alejandro Durán/THE CITY</figcaption>
          </div>
          <p className="copy">
            <b>
              What has your experience been like in school since losing your
              mom?
            </b>
          </p>
          <p className="copy">
            Now that she's not here anymore, I don't really have that motivation
            anymore. My grades are still doing good. It used to be better when
            she was still here. I’d be proud, going to school. Now, I just go to
            school because I want to get an education and go home.
          </p>
          <p className="copy">
            <b>What do you wish teachers understood about losing a parent?</b>
          </p>
          <p className="copy">
            It's not the easiest thing. It impacts me and my classwork.
            Sometimes, I would just think of my mom and stuff and have a little
            mental breakdown. I would just take a walk. They would get mad
            usually. If that was something they knew and wouldn't get mad at, I
            would have appreciated that.
          </p>
          <p className="copy">
            <b>
              What advice would you give to other kids who are going through the
              same thing?
            </b>
          </p>
          <p className="copy">
            I’d just say keep your head up, like, this is how life is. Sometimes
            you gotta move on. At the same time, it's not gonna be easy. But
            eventually you're gonna have to.
          </p>
          <p className="copy">
            <b>
              When you’re sad and you’re missing your mom, what do you do to
              feel better?
            </b>
          </p>
          <p className="copy">
            I just think, like, this really happened? It's just crazy sad. But I
            know she wouldn't want me to be like this. She would have wanted me
            to go on with my life and not to think of her as much. I know that
            she's in a better place. That's what cheers me up a bit.
          </p>
        </div>
      </div>

      {/* FOOTER SLIDE */}
      <div className="scroll-container">
        <div className="copy-container">
          <div className="photo">
            <LazyLoadImage
              src={require("../assets/images/012023_14_rana_ibrahim.jpeg")}
              alt="Rana holds up a photo on her phone of her mom Fatma"
            />
            <figcaption>Hiram Alejandro Durán/THE CITY</figcaption>
          </div>
          <p className="copy">
            This story was produced by THE CITY, Columbia Journalism
            Investigations, Type Investigations and City Limits as part of "
            <OutboundLink to="https://projects.thecity.nyc/covid-19-deaths/">
              MISSING THEM
            </OutboundLink>
            ," THE CITY’s COVID memorial and journalism project.
          </p>
          <p className="copy">
            Do you know a child who has lost a parent or caregiver to COVID-19?{" "}
            <OutboundLink to="https://docs.google.com/forms/d/e/1FAIpQLScMvbjhnKFUledR3qSWcWkIaaB7QefVOs5l_Z_b6-g2Y5kccA/viewform">
              Tell us more here
            </OutboundLink>
            . If you know someone who died due to COVID,{" "}
            <OutboundLink to="https://docs.google.com/forms/d/e/1FAIpQLSfw1r9xyXVA9CjeMP2V8xr1ZszLr4ZgCOtHB7kLTzPaGrXd9A/viewform">
              share their story here
            </OutboundLink>{" "}
            or email us at{" "}
            <OutboundLink to="mailto:memorial@thecity.nyc">
              memorial@thecity.nyc
            </OutboundLink>
            .
          </p>
          <Footer />
        </div>
      </div>
    </AwesomeSlider>
  </PageLayout>
);
